/**
 * Which community codes a person's identifier, and what that identifier will be.
 *
 * Someone in Joyya Goods **and** Khal Par is coded KP — their location — whichever order the two
 * were picked in. The first build of the registration form asked for "the" community in one box
 * and "also part of" in a second, and relied on a hint to say which went where; centre managers
 * put Joyya Goods first and the person was coded JG.
 *
 * Mirrors `backend/src/graphql/utils/personIdentifier.ts`, which refuses a save that gets this
 * wrong. Pure on purpose: the rules are the part worth testing, and the form only wires them up.
 */

export interface CommunityOption {
    id: number;
    code: string;
    name: string;
    /** A place, not a programme. Unmarked organisations keep "first chosen codes the ID". */
    isLocation: boolean;
}

export type FormulaPart =
    | { kind: 'group'; groupId: number }
    | { kind: 'key'; groupId: number };

/**
 * `"2+{key:2}"` → the community group, then that group's running number.
 *
 * Null for an organisation with no formula, or one this form cannot fill — it chooses exactly one
 * community, so a formula naming two separate groups is left to the classic form rather than
 * half-built here.
 */
export const parseFormula = (formula: string | null | undefined): FormulaPart[] | null => {
    if (formula == null || formula.trim() === '') return null;

    const parts = formula.split('+').map((raw): FormulaPart | null => {
        const key = raw.match(/^\{key:(\d+)\}$/);
        if (key != null) return { kind: 'key', groupId: Number(key[1]) };
        if (/^\d+$/.test(raw.trim())) return { kind: 'group', groupId: Number(raw.trim()) };
        return null;
    });

    if (parts.some((p) => p == null)) return null;

    const groups = (parts as FormulaPart[]).filter((p) => p.kind === 'group');
    if (groups.length !== 1) return null;

    return parts as FormulaPart[];
};

/** The one community group the formula codes from. */
export const communityGroupOf = (parts: FormulaPart[]): number =>
    parts.find((p) => p.kind === 'group')!.groupId;

export type CodingChoice =
    | { status: 'none' }
    /** More than one location was chosen and none has been picked as home yet. */
    | { status: 'chooseHome'; locations: CommunityOption[] }
    | { status: 'ok'; community: CommunityOption };

/**
 * The community that codes the identifier.
 *
 * - One location among the chosen → it, wherever it sits in the list.
 * - Several → the one marked as home; until then, ask. Not refused: someone can live between two.
 * - None (programme-only, or an organisation with no locations marked) → the first chosen, which
 *   is exactly the old behaviour.
 *
 * `selected` is in the order the communities were picked.
 */
export const chooseCodingCommunity = (selected: CommunityOption[], homeLocationId: number | null): CodingChoice => {
    if (selected.length === 0) return { status: 'none' };

    const locations = selected.filter((c) => c.isLocation);

    if (locations.length === 1) return { status: 'ok', community: locations[0] };

    if (locations.length > 1) {
        const home = locations.find((c) => c.id === homeLocationId);
        return home != null ? { status: 'ok', community: home } : { status: 'chooseHome', locations };
    }

    return { status: 'ok', community: selected[0] };
};

/**
 * Fill the formula: the community's code for the group, then its next number.
 *
 * A code with no counter yet starts at 1 — what the server does when it first sees one.
 */
export const buildIdentifier = (
    parts: FormulaPart[],
    code: string,
    keyCounters: Array<{ key: string; next: number }>,
): string =>
    parts.map((p) => {
        if (p.kind === 'group') return code;
        return String(keyCounters.find((k) => k.key === code)?.next ?? 1);
    }).join('');
